import Lottie from "lottie-react";
import { MouseEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";

import avec from "./avec.json";
import kkiosk from "./kkiosk.json";
import oxxo from "./oxxo.json";
import pressAndBooks from "./pressAndBooks.json";
import kitchenMenuLogo from "../../../assets/kitchen-menu.svg";
import { ActionKind } from "../../types/Action";
import { Tenant } from "../../types/Config";
import { useConfig } from "../ConfigProvider";
import useBarcodeScanner, {
    requiresOperatorBadgeScan
} from "../hooks/useBarcodeScanner";
import {
    RootStateKind,
    useRootState,
    WelcomeSubStateKind
} from "../state/StateProvider";
import * as svg from "../svg";

export const getAnimation = (tenant: Tenant) => {
    switch (tenant) {
        case "avec":
            return (
                <Lottie
                    data-testid="screensaver"
                    animationData={avec}
                    loop
                    autoplay
                    muted
                    className="fixed top-0 h-screen w-screen object-cover"
                />
            );
        case "kkiosk":
            return (
                <Lottie
                    data-testid="screensaver"
                    animationData={kkiosk}
                    loop
                    autoplay
                    muted
                    className="fixed top-0 h-screen w-screen object-cover"
                />
            );
        case "oxxo":
            return (
                <Lottie
                    data-testid="screensaver"
                    animationData={oxxo}
                    loop
                    autoplay
                    muted
                    className="fixed top-0 h-screen w-screen object-cover"
                />
            );
        case "pressAndBooks":
            return (
                <Lottie
                    data-testid="screensaver"
                    animationData={pressAndBooks}
                    loop
                    autoplay
                    muted
                    className="fixed top-0 h-screen w-screen object-cover"
                />
            );
        default:
            return tenant satisfies never;
    }
};

const StaffedScreenSaver = () => {
    const { t } = useTranslation();
    const { state, dispatch } = useRootState(RootStateKind.Welcome);
    const { tenant, kitchen, reload } = useConfig();
    const hasOpenKitchen = kitchen.hasKitchen && kitchen.isOpen;

    useEffect(() => {
        if (!kitchen.hasKitchen) {
            return;
        }

        const timer = setTimeout(() => {
            reload();
        }, kitchen.secondsUntilTransition * 1000);

        return () => clearTimeout(timer);
    }, [kitchen]);

    const onStart = (e: MouseEvent) => {
        e.stopPropagation();
        dispatch({ kind: ActionKind.Start });
    };

    const onStartKitchen = (e: MouseEvent) => {
        e.stopPropagation();
        dispatch({ kind: ActionKind.Start, openKitchen: true });
    };

    return (
        <button
            className="flex h-full flex-1 flex-col items-center justify-end gap-6"
            disabled={state.subState.kind === WelcomeSubStateKind.Loading}
            onClick={onStart}
        >
            {getAnimation(tenant)}
            <div className="z-10 flex h-full w-full flex-col items-center justify-center gap-6">
                <div
                    data-testid="start-shopping"
                    className="grid h-[150px] w-[950px] grid-cols-4 items-center justify-items-center gap-2 rounded-cta bg-welcome-background text-[48px] font-bold text-welcome-foreground transition duration-100 ease-in-out active:scale-95 active:text-button-secondary-hover active:shadow-inner"
                    onClick={onStart}
                >
                    <div />
                    <div className="col-span-2 grow whitespace-nowrap">
                        {t("startShopping")}
                    </div>
                    <div>{svg.play}</div>
                </div>
                {hasOpenKitchen && (
                    <div
                        className="z-10 flex w-full justify-center px-8"
                        data-theme="kitchen"
                    >
                        <div
                            data-testid="start-shopping-kitchen"
                            className="grid h-[150px] w-[950px] grid-cols-4 items-center justify-items-center gap-2 rounded-cta bg-primary-500 text-[48px] font-bold text-white transition duration-100 ease-in-out hover:text-primary-100 active:scale-95 active:text-primary-100 active:shadow-inner"
                            onClick={onStartKitchen}
                        >
                            <img
                                src={kitchenMenuLogo}
                                className="mx-auto h-20 w-auto"
                            />
                            <div className="col-span-2 grow whitespace-nowrap">
                                {t("startShoppingKitchen")}
                            </div>
                            <div>{svg.play}</div>
                        </div>
                    </div>
                )}
            </div>
        </button>
    );
};

export const Screensaver = () => {
    const { state, dispatch } = useRootState(RootStateKind.Welcome);

    useBarcodeScanner({
        onScan: (code) => {
            if (requiresOperatorBadgeScan(state)) {
                dispatch({
                    kind: ActionKind.ScanOperatorBadge,
                    scannedBadge: code
                });
            } else {
                dispatch({ kind: ActionKind.Start, scannedCode: code });
            }
        }
    });

    return <StaffedScreenSaver />;
};
